import { DatePipe, DecimalPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { CatalogService, MarketplaceOfferingProposal } from './catalog.service';
import {
  MARKETPLACE_LIST_TOOLBAR_STYLES,
  MarketplaceLiveSubscription,
  liveStatusKey,
  subscribeMarketplaceOperationsLiveUpdates,
  unsubscribeMarketplaceOperationsLiveUpdates
} from './marketplace-list-page.utils';

@Component({
  standalone: true,
  selector: 'ain-marketplace-offering-proposals',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    TableModule,
    ButtonModule,
    DialogModule,
    FormsModule,
    InputTextModule,
    SelectModule,
    TextareaModule,
    DatePipe,
    DecimalPipe,
    RouterLink
  ],
  styles: [MARKETPLACE_LIST_TOOLBAR_STYLES],
  template: `
    <ain-page
      [title]="t('catalog.marketplace.proposals.title')"
      [description]="t('catalog.marketplace.proposals.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'catalog.marketplace.proposals.help.title'"
        [descriptionKey]="'catalog.marketplace.proposals.help.description'"
        [bulletKeys]="[
          'catalog.marketplace.proposals.help.bullets.review',
          'catalog.marketplace.proposals.help.bullets.approve',
          'catalog.marketplace.proposals.help.bullets.live'
        ]" />
      <p class="back"><a routerLink="/marketplace/dashboard">{{ t('catalog.marketplace.dashboard.title') }}</a></p>
      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else {
        <section class="card panel">
          <div class="toolbar">
            <button pButton type="button" severity="secondary" [label]="t('catalog.marketplace.actions.refresh')" (click)="load()"></button>
            <span class="live-status" [class]="liveUpdateService.connectionState()">
              {{ t(liveStatusKey(liveUpdateService.connectionState())) }}
            </span>
          </div>
          <div class="filter-grid">
            <label>
              <span>{{ t('catalog.marketplace.filters.status') }}</span>
              <p-select
                [options]="statusOptions"
                optionValue="value"
                optionLabel="labelKey"
                [(ngModel)]="statusFilter"
                (ngModelChange)="load()"
                [showClear]="true"
                appendTo="body">
                <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : t('catalog.marketplace.proposals.filters.queueDefault') }}</ng-template>
                <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
              </p-select>
            </label>
          </div>
          @if (info()) {
            <p class="info">{{ info() }}</p>
          }
          @if (rows().length === 0) {
            <ain-state type="empty" [title]="t('catalog.marketplace.proposals.empty')" />
          } @else {
            <p-table [value]="rows()" dataKey="id" [paginator]="true" [rows]="15">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('catalog.marketplace.proposals.columns.seller') }}</th>
                  <th>{{ t('catalog.marketplace.proposals.columns.kind') }}</th>
                  <th>{{ t('catalog.marketplace.proposals.columns.product') }}</th>
                  <th>{{ t('catalog.marketplace.proposals.columns.offerPrice') }}</th>
                  <th>{{ t('catalog.marketplace.proposals.columns.status') }}</th>
                  <th>{{ t('catalog.marketplace.proposals.columns.submitted') }}</th>
                  <th>{{ t('catalog.marketplace.proposals.columns.actions') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>
                    <a [routerLink]="['/marketplace/sellers', row.sellerId]">{{ sellerLabel(row) }}</a>
                  </td>
                  <td>{{ t(proposalKindKey(row.kind)) }}</td>
                  <td>{{ productLabel(row) }}</td>
                  <td>{{ row.offerPrice | number:'1.2-2' }} {{ row.offerCurrencyCode }}</td>
                  <td>{{ t(proposalStatusKey(row.status)) }}</td>
                  <td>{{ row.createdAtUtc | date:'medium' }}</td>
                  <td class="actions">
                    @if (row.status === 'Submitted') {
                      <button pButton type="button" size="small" severity="secondary" [label]="t('catalog.marketplace.proposals.review')" (click)="beginReview(row)"></button>
                    }
                    @if (row.status === 'Submitted' || row.status === 'UnderReview') {
                      <button pButton type="button" size="small" [label]="t('catalog.marketplace.proposals.approve')" (click)="openApprove(row)"></button>
                      <button pButton type="button" size="small" severity="danger" [label]="t('catalog.marketplace.proposals.reject')" (click)="openReject(row)"></button>
                    }
                  </td>
                </tr>
              </ng-template>
            </p-table>
          }
        </section>
      }

      <p-dialog
        [header]="t('catalog.marketplace.proposals.approveTitle')"
        [(visible)]="approveVisible"
        [modal]="true"
        [style]="{ width: '32rem' }">
        <label class="stack">
          <span>{{ t('catalog.marketplace.proposals.reviewNotes') }}</span>
          <textarea pTextarea rows="3" [(ngModel)]="reviewNotes"></textarea>
        </label>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="approveVisible = false"></button>
          <button pButton type="button" [label]="t('catalog.marketplace.proposals.approve')" [disabled]="acting()" (click)="approve()"></button>
        </ng-template>
      </p-dialog>

      <p-dialog
        [header]="t('catalog.marketplace.proposals.rejectTitle')"
        [(visible)]="rejectVisible"
        [modal]="true"
        [style]="{ width: '32rem' }">
        <label class="stack">
          <span>{{ t('catalog.marketplace.proposals.rejectReason') }}</span>
          <textarea pTextarea rows="3" [(ngModel)]="rejectReason"></textarea>
        </label>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="rejectVisible = false"></button>
          <button pButton type="button" severity="danger" [label]="t('catalog.marketplace.proposals.reject')" [disabled]="acting() || !rejectReason.trim()" (click)="reject()"></button>
        </ng-template>
      </p-dialog>
    </ain-page>
  `
})
export class MarketplaceOfferingProposalsComponent implements OnInit, OnDestroy {
  private readonly service = inject(CatalogService);
  private readonly contextService = inject(CurrentContextService);
  readonly liveUpdateService = inject(LiveUpdateService);
  private readonly localization = inject(LocalizationService);
  private liveSubscription: MarketplaceLiveSubscription = { topic: null, eventsSubscription: null };

  readonly loading = signal(true);
  readonly acting = signal(false);
  readonly rows = signal<MarketplaceOfferingProposal[]>([]);
  readonly info = signal('');

  statusFilter: string | null = null;
  approveVisible = false;
  rejectVisible = false;
  reviewNotes = '';
  rejectReason = '';
  private activeRow: MarketplaceOfferingProposal | null = null;

  readonly statusOptions = [
    { value: 'Submitted', labelKey: 'catalog.marketplace.proposals.status.submitted' },
    { value: 'UnderReview', labelKey: 'catalog.marketplace.proposals.status.underReview' },
    { value: 'Approved', labelKey: 'catalog.marketplace.proposals.status.approved' },
    { value: 'Rejected', labelKey: 'catalog.marketplace.proposals.status.rejected' },
    { value: 'Withdrawn', labelKey: 'catalog.marketplace.proposals.status.withdrawn' }
  ];

  liveStatusKey = liveStatusKey;

  ngOnInit(): void {
    this.load();
    void this.startLiveUpdates();
  }

  ngOnDestroy(): void {
    void unsubscribeMarketplaceOperationsLiveUpdates(this.liveUpdateService, this.liveSubscription);
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  load(): void {
    this.loading.set(true);
    this.info.set('');
    this.service.listMarketplaceOfferingProposals(this.statusFilter ?? undefined).subscribe({
      next: rows => {
        this.rows.set(rows);
        this.loading.set(false);
      },
      error: () => {
        this.rows.set([]);
        this.loading.set(false);
      }
    });
  }

  productLabel(row: MarketplaceOfferingProposal): string {
    if (row.kind === 'LinkExistingOffering' && row.targetOfferingId) {
      return row.targetOfferingId;
    }
    return row.proposedTitle || row.proposedCode || '—';
  }

  sellerLabel(row: MarketplaceOfferingProposal): string {
    const name = row.sellerDisplayName?.trim();
    const code = row.sellerCode?.trim();
    if (code && name) {
      return `${code} — ${name}`;
    }
    if (name) {
      return name;
    }
    if (code) {
      return code;
    }
    return row.sellerId;
  }

  proposalKindKey(kind: string): string {
    return kind === 'LinkExistingOffering'
      ? 'catalog.marketplace.proposals.kind.linkExisting'
      : 'catalog.marketplace.proposals.kind.newOffering';
  }

  proposalStatusKey(status: string): string {
    const map: Record<string, string> = {
      Draft: 'catalog.marketplace.proposals.status.draft',
      Submitted: 'catalog.marketplace.proposals.status.submitted',
      UnderReview: 'catalog.marketplace.proposals.status.underReview',
      Approved: 'catalog.marketplace.proposals.status.approved',
      Rejected: 'catalog.marketplace.proposals.status.rejected',
      Withdrawn: 'catalog.marketplace.proposals.status.withdrawn'
    };
    return map[status] ?? status;
  }

  beginReview(row: MarketplaceOfferingProposal): void {
    this.acting.set(true);
    this.service.beginReviewMarketplaceOfferingProposal(row.id, {}).subscribe({
      next: () => {
        this.acting.set(false);
        this.info.set(this.t('catalog.marketplace.proposals.reviewStarted'));
        this.load();
      },
      error: () => this.acting.set(false)
    });
  }

  openApprove(row: MarketplaceOfferingProposal): void {
    this.activeRow = row;
    this.reviewNotes = '';
    this.approveVisible = true;
  }

  openReject(row: MarketplaceOfferingProposal): void {
    this.activeRow = row;
    this.rejectReason = '';
    this.rejectVisible = true;
  }

  approve(): void {
    if (!this.activeRow) {
      return;
    }
    this.acting.set(true);
    this.service.approveMarketplaceOfferingProposal(this.activeRow.id, { notes: this.reviewNotes.trim() || undefined }).subscribe({
      next: () => {
        this.acting.set(false);
        this.approveVisible = false;
        this.info.set(this.t('catalog.marketplace.proposals.approved'));
        this.load();
      },
      error: () => this.acting.set(false)
    });
  }

  reject(): void {
    if (!this.activeRow || !this.rejectReason.trim()) {
      return;
    }
    this.acting.set(true);
    this.service.rejectMarketplaceOfferingProposal(this.activeRow.id, { reason: this.rejectReason.trim() }).subscribe({
      next: () => {
        this.acting.set(false);
        this.rejectVisible = false;
        this.info.set(this.t('catalog.marketplace.proposals.rejected'));
        this.load();
      },
      error: () => this.acting.set(false)
    });
  }

  private async startLiveUpdates(): Promise<void> {
    this.liveSubscription = await subscribeMarketplaceOperationsLiveUpdates(
      this.contextService.context()?.tenantId,
      this.liveUpdateService,
      () => this.load());
  }
}
